import { useEffect, useState } from "react";
import { useDataProvider } from "react-admin";
import {
  buildFmbSabilMatchFromRows,
  emptyFmbSabilMatch,
  type FmbSabilMatch,
} from "./fmb-sabil-match";

function normalizeIts(value: unknown): string {
  if (value == null) return "";
  return String(value).trim();
}

function looksLikeItsComplete(value: unknown): boolean {
  const its = normalizeIts(value);
  if (!its) return false;
  if (!/^\d+$/.test(its)) return false;
  return its.length >= 5;
}

/**
 * Live sabil + directory lookup for FMB create/edit:
 * open sabils on this ITS or the person's HOF ITS, and whether ITS is in local itsdata.
 */
export function useFmbSabilMatch(itsNo: unknown): {
  match: FmbSabilMatch;
  checking: boolean;
  inDirectory: boolean | null;
} {
  const dataProvider = useDataProvider();
  const [match, setMatch] = useState<FmbSabilMatch>(emptyFmbSabilMatch());
  const [inDirectory, setInDirectory] = useState<boolean | null>(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    const its = normalizeIts(itsNo);
    if (!looksLikeItsComplete(its)) {
      setMatch(emptyFmbSabilMatch());
      setInDirectory(null);
      setChecking(false);
      return;
    }

    let alive = true;
    setChecking(true);
    const timer = window.setTimeout(() => {
      (async () => {
        try {
          const itsRes = await dataProvider.getMany("itsdata", { ids: [its] });
          const itsRow = Array.isArray(itsRes?.data) ? itsRes.data[0] : undefined;
          const foundInDirectory = Boolean(itsRow);
          const hofItsNo = normalizeIts((itsRow as { HOF_ID?: string } | undefined)?.HOF_ID);
          const lookupIts = Array.from(
            new Set([its, hofItsNo].filter((v) => looksLikeItsComplete(v)))
          );

          const sabilBatches = await Promise.all(
            lookupIts.map((candidate) =>
              dataProvider.getList("sabilData", {
                pagination: { page: 1, perPage: 25 },
                sort: { field: "updatedAt", order: "DESC" },
                filter: { q: candidate, status: "ACTIVE" },
              })
            )
          );

          if (!alive) return;
          const rows = sabilBatches.flatMap((res) =>
            Array.isArray(res?.data) ? res.data : []
          ) as Array<{
            id?: string;
            sabilNo?: string | null;
            sabilType?: string | null;
            itsNo?: string | null;
            name?: string | null;
          }>;

          setInDirectory(foundInDirectory);
          setMatch(
            buildFmbSabilMatchFromRows({
              fmbItsNo: its,
              hofItsNo: hofItsNo || null,
              sabilRows: rows,
            })
          );
        } catch {
          if (!alive) return;
          setInDirectory(null);
          setMatch(emptyFmbSabilMatch());
        } finally {
          if (alive) setChecking(false);
        }
      })();
    }, 300);

    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [itsNo, dataProvider]);

  return { match, checking, inDirectory };
}
